import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  withRouter,
  PageHeader,
  Head,
  PaginationControl,
  PerformerAppPath,
  PerformerAppSmartBanner,
  RouterProps,
  isSmallDeviceWidth,
} from "component-library";
import { i18n, withTranslation, WithTranslation } from "i18n";
import {
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
} from "shared-services";
import { MapDispatchToProps } from "shared/types/utilities";

import {
  PAGINATION_PAGE_RANGE_DESKTOP,
  PAGINATION_PAGE_RANGE_MOBILE,
  PageTitleKey,
  SELF_TAPE_DETAIL_FULL_PATH,
} from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IWithRequestAbortController } from "types/axios";

import {
  ISelfTapeRequestNotification,
  ISelfTapeRequestNotificationFilterParams,
  ISelfTapeRequestNotificationFilters,
  ISelfTapeRequestNotificationListPaginated,
  ISelfTapeRequestNotificationStatus,
} from "models/selfTapeRequest";

import { getPageTitle } from "helpers/getPageTitle";
import selfTapeRequestNotificationService from "services/api/selfTapeRequest/selfTapeRequestNotificationService";

import withSelfTapeRequestNotificationsFilter, {
  IWithSelfTapeRequestNotificationsFilterOptions,
  IWithSelfTapeRequestNotificationsFilterProps,
  IWithSelfTapeRequestNotificationsFilterTexts,
} from "hoc/withSelfTapeRequestNotificationsFilter/withSelfTapeRequestNotificationsFilter";
import { FilterType } from "hoc/withSelfTapeRequestNotificationsFilter/withSelfTapeRequestNotificationsFilter.config";

import { JobsPageTab } from "pages/JobsPage/JobsPage.config";

import SelfTapeRequestNotificationTable, {
  ISelfTapeRequestNotificationTableTexts,
} from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable";
import { prepareTableTexts } from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.helpers";
import {
  ISelfTapeRequestNotificationTableItem,
  SelfTapeRequestNotificationUuid,
} from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.models";

import {
  prepareFilterControlTexts,
  prepareSelfTapeRequestItem,
} from "./SelfTapeRequestNotificationsPage.helpers";
import SelectAllControl from "components/SelectAllControl/SelectAllControl";
import PaginationResultStats from "components/PaginationResultStats/PaginationResultStats";
import RefreshButton from "components/RefreshButton/RefreshButton";

import "./SelfTapeRequestNotificationsPage.scss";
import { generatePath } from "react-router-dom";

export interface ISelfTapeRequestNotificationsPageTexts {
  table: ISelfTapeRequestNotificationTableTexts;
  pageTitle: string;
}

export interface ISelfTapeRequestNotificationsPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export type ISelfTapeRequestNotificationsPageStoreProps = {
  texts: ISelfTapeRequestNotificationsPageTexts;
  filterControlTexts: IWithSelfTapeRequestNotificationsFilterTexts;
  convertToTableItems: (
    item: ISelfTapeRequestNotification[]
  ) => ISelfTapeRequestNotificationTableItem[];
  getSelfTapeRequestNotificationFilters(
    abortController?: AbortController
  ): Promise<ISelfTapeRequestNotificationFilters>;
  findSelfTapeRequestNotifications(
    params: ISelfTapeRequestNotificationFilterParams,
    abortController?: AbortController
  ): Promise<ISelfTapeRequestNotificationListPaginated>;
  updateStatus(
    statuses: ISelfTapeRequestNotificationStatus[],
    abortController?: AbortController
  ): Promise<void>;
};

export interface ISelfTapeRequestNotificationsPageProps
  extends
    ISelfTapeRequestNotificationsPageStoreProps,
    RouterProps,
    ISelfTapeRequestNotificationsPageActions,
    IWithSelfTapeRequestNotificationsFilterProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export interface ISelfTapeRequestNotificationsPageState {
  selectedItems: SelfTapeRequestNotificationUuid[];
}

export class SelfTapeRequestNotificationsPage
  extends React.Component<
    ISelfTapeRequestNotificationsPageProps,
    ISelfTapeRequestNotificationsPageState
  >
  implements IWithRequestAbortController
{
  public static pageSize: number = 50;
  public abortController = new AbortController();

  public readonly state: Readonly<ISelfTapeRequestNotificationsPageState> = {
    selectedItems: [],
  };

  public componentWillUnmount() {
    this.abortController.abort?.();
  }

  public render() {
    const { t } = i18n;
    const {
      texts,
      renderSelfTapeRequestNotificationsFilterControl,
      filterValues,
      paginatedItems,
      isLoading,
    } = this.props;
    const pageTitle = t("common:mediaRequest.page.title");

    return (
      <section className="c-self-tape-request-notifications-page">
        <Head registerPageView={this.registerPageView}>
          <title>{pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.TapeRequests} />
        <PageHeader pageTitle={pageTitle} />
        {renderSelfTapeRequestNotificationsFilterControl(
          this.renderPagination()
        )}
        <div className="g-content-area mt-1">
          <div className="g-col-lg-12">
            <div className="flex flex-col-reverse flex-wrap items-start justify-between gap-3 md:flex-row">
              <div className="flex flex-col gap-2">
                <PaginationResultStats
                  page={filterValues[FilterType.PageNumber]}
                  itemsPerPage={SelfTapeRequestNotificationsPage.pageSize}
                  itemsTotal={paginatedItems.count}
                  textTemplate={texts.table.resultStats}
                />
                <SelectAllControl
                  selectItems={this.selectItems}
                  deselectItems={this.deselectItems}
                  selectedItems={this.state.selectedItems}
                  markItemsAsRead={this.markSelectedItemsAsRead}
                  markItemsAsUnread={this.markSelectedItemsAsUnread}
                  items={paginatedItems.items}
                />
              </div>
              <div className="w-full md:flex md:w-auto md:justify-end">
                <RefreshButton
                  onClick={this.handleRefresh}
                  className="!flex !aspect-square !h-10 !w-10 !items-center !justify-center !gap-[10px] !rounded-[10px] !border !border-grey-light-2"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <SelfTapeRequestNotificationTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={this.state.selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onItemClick={this.handleItemClick}
              itemsPerPage={SelfTapeRequestNotificationsPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              returnUri="#"
              showTopControls={false}
            />
          </div>
        </div>
      </section>
    );
  }

  private deselectItems = (itemIds: SelfTapeRequestNotificationUuid[]) =>
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });

  private selectItems = (itemIds: SelfTapeRequestNotificationUuid[]) =>
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });

  private handleItemClick = async (
    item: ISelfTapeRequestNotificationTableItem
  ) => {
    const { router } = this.props;
    await this.handleMarkAsRead([item.id]);

    item.sendStats();

    router.navigate(
      generatePath(SELF_TAPE_DETAIL_FULL_PATH, { performerRequestId: item.id })
    );
  };

  private handleUpdatePageNumber = (page: number) => {
    const { router } = this.props;
    const [, setSearchParams] = router.searchParams;

    setSearchParams({ page: String(page) });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (
    itemIds: SelfTapeRequestNotificationUuid[]
  ) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
  };

  private handleMarkAsUnread = async (
    itemIds: SelfTapeRequestNotificationUuid[]
  ) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
  };

  private markSelectedItemsAsRead = (
    itemIds: SelfTapeRequestNotificationUuid[]
  ) => {
    void this.handleMarkAsRead(itemIds);
  };

  private markSelectedItemsAsUnread = (
    itemIds: SelfTapeRequestNotificationUuid[]
  ) => {
    void this.handleMarkAsUnread(itemIds);
  };

  private handleRefresh = () => {
    this.props.changeFilters({
      [FilterType.PageNumber]: this.props.filterValues[FilterType.PageNumber],
    });
    this.props.refreshCounters();
  };

  private renderPagination = () => {
    const { paginatedItems, filterValues } = this.props;
    const pageNumber = filterValues[FilterType.PageNumber];
    const pageCount = Math.ceil(
      paginatedItems.count / SelfTapeRequestNotificationsPage.pageSize
    );

    return (
      <div className="ml-auto">
        <PaginationControl
          handlePageChange={this.handleUpdatePageNumber}
          pageRangeDisplayed={
            isSmallDeviceWidth()
              ? PAGINATION_PAGE_RANGE_MOBILE
              : PAGINATION_PAGE_RANGE_DESKTOP
          }
          pageCount={pageCount}
          selectedPage={pageNumber}
          initialSelectedPage={pageNumber}
          arePageBreaksMandatory
        />
      </div>
    );
  };

  private registerPageView = () => {
    // register GTMEvent in a future ticket
  };
}

const mapStateToProps = (
  state: IApplicationState,
  props: ISelfTapeRequestNotificationsPageProps
): ISelfTapeRequestNotificationsPageStoreProps => {
  const commonTranslations = state.translation.common;
  const pageTranslation =
    state.translation.pages[PageKey.SelfTapeRequestNotifications]!;
  const userType = state.user.data!.type;

  return {
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(
        commonTranslations,
        PageTitleKey.JobsFeedSelfTapeRequests
      ),
    },
    convertToTableItems: (items) =>
      items.map((item) =>
        prepareSelfTapeRequestItem({ item, userType, t: props.t })
      ),
    filterControlTexts: prepareFilterControlTexts(pageTranslation),
    findSelfTapeRequestNotifications: (...args) =>
      selfTapeRequestNotificationService.find(...args),
    getSelfTapeRequestNotificationFilters: (...args) =>
      selfTapeRequestNotificationService.getFilters(...args),
    updateStatus: (...args) =>
      selfTapeRequestNotificationService.updateStatus(...args),
  };
};

const mapDispatchToProps: MapDispatchToProps<ISelfTapeRequestNotificationsPageActions> =
  {
    setLoading,
    showPopup,
    hidePopup,
  };

export const withSelfTapeRequestNotificationsFilterOptions: IWithSelfTapeRequestNotificationsFilterOptions =
  {
    tab: JobsPageTab.SelfTapeRequests,
  };

export default compose(
  withRouter,
  withTranslation(),
  withLaunchDarklyConsumerHOC(),
  connect(mapStateToProps, mapDispatchToProps),
  withSelfTapeRequestNotificationsFilter<ISelfTapeRequestNotificationsPageProps>(
    withSelfTapeRequestNotificationsFilterOptions
  )
)(SelfTapeRequestNotificationsPage);
